import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { Link, Text, mergeStyleSets, TeachingBubble, Stack, DirectionalHint, FontIcon, DefaultButton } from '@fluentui/react';
import Ribbon from '@shared/components/ribbon';
import { LinkedinUpcomingEvent } from '@appsource/components/linkedin/linkedinUpcomingEvent';
import { LinkedinFeaturedCustomer } from '@appsource/components/linkedin/linkedinFeaturedCustomer';
import { CommunicationColors, NeutralColors } from '@fluentui/theme';
import { FeatureFeedback, FeatureFeedbackSubmittedModal } from '@shared/components/featureFeedback';
import { Constants } from '@shared/utils/constants';
import { getNpsModule } from '@appsource/utils/nps';
import { popupDisplayed, PopupType, shouldDisplayPopup } from '@shared/utils/popupDisplayManager';
import { withRouter } from '@shared/routerHistory';
import { SpzaInstrumentService } from 'services/telemetry/spza/spzaInstrument';
import { logger } from '@src/logger';
const smallTextSize = 12;
const normalTextSize = 14;
const largeTextSize = 18;
const contentStyles = mergeStyleSets({
    linkedin: {
        minHeight: 200,
        paddingBottom: 20,
        paddingTop: 32
    },
    featuredCustomersPanel: {
        marginTop: 44
    },
    featuredCustomersTitle: {
        fontSize: largeTextSize,
        fontWight: 400,
        lineHeight: 24
    },
    featuredCustomersList: {
        marginTop: 16,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, 240px)',
        gridGap: 24
    },
    featuredCustomersRibbon: {
        marginTop: 16,
        display: 'grid',
        '.ribbon': {
            marginTop: 0,
            '.ribbonTiles': {
                marginLeft: 0,
                '.ribbonSlider': {
                    height: 'auto'
                },
                '.slick-list': {
                    marginLeft: 16
                }
            }
        }
    },
    upcomingEventLargeIconImg: {
        position: 'absolute',
        display: 'block',
        width: '100%',
        height: '100%'
    },
    upcomingEventNameText: {
        marginTop: 8,
        fontSize: largeTextSize,
        lineHeight: 24,
        fontWeight: 600,
        color: NeutralColors.gray160
    },
    upcomingEventNormalText: {
        marginTop: 8,
        fontSize: smallTextSize,
        lineHeight: 16,
        fontWeight: 600,
        color: NeutralColors.gray130
    },
    upcomingEventLinkButton: {
        width: 212,
        marginTop: 24,
        fontSize: normalTextSize,
        lineHeight: 20,
        fontWeight: 600,
        color: NeutralColors.gray190
    },
    externallinkIcon: {
        marginLeft: 8,
        height: 24,
        fontSize: normalTextSize,
        color: NeutralColors.gray190
    },
    upcomingEventsTitlePanel: {
        marginTop: 44,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start'
    },
    emptyEventsPanel: {
        paddingTop: 8
    },
    emptyEventsIcon: {
        fontSize: 22,
        color: '#69AFE5'
    },
    emptyEventsText: {
        lineHeight: 24,
        fontWeight: 400,
        padding: 8,
        fontSize: normalTextSize,
        color: NeutralColors.gray130
    },
    upcomingEventsTitle: {
        fontSize: largeTextSize,
        fontWeight: 400,
        lineHeight: 24
    },
    upcomingEventsLink: {
        marginLeft: 8,
        fontSize: normalTextSize,
        fontWeight: 400,
        lineHeight: 24,
        verticalAlignment: 'center',
        textDecoration: 'underline'
    },
    upcomingEventsList: {
        marginTop: 16,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, 512px)',
        gridGap: 24
    },
    feedbackTitle: {
        fontSize: 18,
        fontWeight: 400,
        lineHeight: 24
    },
    feedbackItem: {
        marginTop: 16,
        fontSize: normalTextSize,
        fontWeight: 400,
        lineHeight: 20
    },
    feedbackItemBold: {
        marginLeft: 4,
        fontSize: normalTextSize,
        fontWeight: 600,
        lineHeight: 20
    },
    linkedinLogoIcon: {
        fontSize: 20,
        lineHeight: 20,
        verticalAlignment: 'center',
        color: CommunicationColors.primary
    }
});
const MAX_CUSTOMERS_SLIDES = 4;
export function FeaturedCustomersList({ linkedInProductGroup, offerName, locale, countryCode }, context) {
    var _linkedInProductGroup_featuredCustomers;
    if (((_linkedInProductGroup_featuredCustomers = linkedInProductGroup.featuredCustomers) === null || _linkedInProductGroup_featuredCustomers === void 0 ? void 0 : _linkedInProductGroup_featuredCustomers.length) > 0) {
        const customerElements = linkedInProductGroup.featuredCustomers.map((customer, index)=>{
            return /*#__PURE__*/ _jsx(LinkedinFeaturedCustomer, {
                locale: locale,
                countryCode: countryCode,
                customer: customer
            }, 'featuredCustomer_' + index.toString());
        });
        return /*#__PURE__*/ _jsxs("div", {
            className: contentStyles.featuredCustomersPanel,
            children: [
                /*#__PURE__*/ _jsx(Text, {
                    className: contentStyles.featuredCustomersTitle,
                    children: context.locParams('Linked_Featured_Customers_Title', [
                        offerName
                    ])
                }),
                /*#__PURE__*/ _jsx("div", {
                    children: customerElements.length < MAX_CUSTOMERS_SLIDES ? /*#__PURE__*/ _jsx("div", {
                        className: contentStyles.featuredCustomersList,
                        children: customerElements
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: contentStyles.featuredCustomersRibbon,
                        children: /*#__PURE__*/ _jsx(Ribbon, {
                            carousel: true,
                            children: customerElements
                        })
                    })
                })
            ]
        });
    }
    return null;
}
export function UpcomingEventsList({ linkedInProductGroup, offerName, locale, countryCode, onExternalLinkClick }, context) {
    var _linkedInProductGroup_upcomingEvents;
    if (((_linkedInProductGroup_upcomingEvents = linkedInProductGroup.upcomingEvents) === null || _linkedInProductGroup_upcomingEvents === void 0 ? void 0 : _linkedInProductGroup_upcomingEvents.length) > 0) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Stack, {
                    className: contentStyles.upcomingEventsTitlePanel,
                    children: [
                        /*#__PURE__*/ _jsxs(Text, {
                            className: contentStyles.upcomingEventsTitle,
                            children: [
                                " ",
                                context.locParams('Linkedin_Events_Title', [
                                    offerName
                                ])
                            ]
                        }),
                        linkedInProductGroup.eventsUrl && /*#__PURE__*/ _jsx(Link, {
                            className: contentStyles.upcomingEventsLink,
                            target: "_blank",
                            href: linkedInProductGroup.eventsUrl,
                            onClick: onExternalLinkClick,
                            children: context.loc('Linkedin_Events_Link')
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: contentStyles.upcomingEventsList,
                    children: linkedInProductGroup.upcomingEvents.map((event, index)=>{
                        return /*#__PURE__*/ _jsx(LinkedinUpcomingEvent, {
                            locale: locale,
                            countryCode: countryCode,
                            event: event,
                            onExternalLinkClick: onExternalLinkClick
                        }, 'upcomingEvent_' + index.toString());
                    })
                })
            ]
        });
    } else return /*#__PURE__*/ _jsxs(Stack, {
        children: [
            /*#__PURE__*/ _jsx(Stack, {
                className: contentStyles.upcomingEventsTitlePanel,
                children: /*#__PURE__*/ _jsxs(Text, {
                    className: contentStyles.upcomingEventsTitle,
                    children: [
                        " ",
                        context.locParams('Linkedin_Events_Title', [
                            offerName
                        ])
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                horizontal: true,
                verticalAlign: "center",
                className: contentStyles.emptyEventsPanel,
                children: [
                    /*#__PURE__*/ _jsx(FontIcon, {
                        iconName: "Calendar",
                        className: contentStyles.emptyEventsIcon
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        className: contentStyles.emptyEventsText,
                        children: context.locParams('Linkedin_Events_Empty_Message', [
                            offerName
                        ])
                    })
                ]
            })
        ]
    });
}
export function FeedbackSection({ onFeedbackClick }, context) {
    return /*#__PURE__*/ _jsxs(Stack, {
        tokens: {
            childrenGap: 16
        },
        horizontalAlign: "start",
        children: [
            /*#__PURE__*/ _jsxs(Stack, {
                tokens: {
                    childrenGap: 12
                },
                horizontal: true,
                verticalAlign: "center",
                children: [
                    /*#__PURE__*/ _jsx(FontIcon, {
                        iconName: "LinkedInLogo",
                        className: contentStyles.linkedinLogoIcon
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        className: contentStyles.feedbackTitle,
                        children: context.loc('Linkedin_Connected_Title', 'Stay connected to the community')
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Text, {
                className: contentStyles.feedbackItem,
                children: [
                    context.loc('Linkedin_Connected_Content_1', 'Explore and connect with other companies interested in this topic. The following information is provided directly from LinkedIn.'),
                    /*#__PURE__*/ _jsx("span", {
                        className: contentStyles.feedbackItemBold,
                        children: context.loc('Linkedin_Connected_Content_2', 'Let us know if this is useful to you!')
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DefaultButton, {
                className: contentStyles.feedbackItem,
                text: context.loc('Linkedin_Feedback_Section_Action', 'Give feedback'),
                onClick: onFeedbackClick
            })
        ]
    });
}
const LinkedinProductGroupFunction = ({ linkedInProductGroup, offerName, locale, countryCode, userEmail, feedbackScoreRequired, location, parentTabQuery }, context)=>{
    const [feedbackMode, setFeedbackMode] = useState(false);
    const [teachingBubbleMode, setTeachingBubbleMode] = useState(false);
    const [feedbackSentMode, setFeedbackSentMode] = useState(false);
    const featureFeedbackContent = {
        title: context.loc('Linkedin_Feedback_Dialog_Title', 'We’d love your feedback'),
        scoreTitle: context.loc('Linkedin_Feedback_Score_Title', 'How valuable do you find the information provided by LinkedIn?'),
        scoreMinLabel: context.loc('Linkedin_Feedback_Score_Min_Title', 'Not valuable'),
        scoreMaxLabel: context.loc('Linkedin_Feedback_Score_Max_Title', 'Very valuable'),
        scoreRequired: feedbackScoreRequired,
        checksTitle: context.loc('Linkedin_Feedback_Checks_Title', 'Which of the following contributed to your experience?'),
        checks: [
            {
                key: 'Featured customers',
                text: context.loc('Linkedin_Feedback_Featured_Customers', 'Featured customers')
            },
            {
                key: 'Upcoming events',
                text: context.loc('Linkedin_Feedback_Upcoming_Events', 'Upcoming events')
            },
            {
                key: 'Direct link to the LinkedIn community',
                text: context.loc('Linkedin_Feedback_Direct_Link', 'Direct link to the LinkedIn community')
            }
        ],
        openQuestionFieldTitle: context.loc('Linkedin_Feedback_Open_Question_Title', 'Which additional information from LinkedIn would you like to see?'),
        openQuestionFieldPlaceholder: context.loc('Linkedin_Feedback_Open_Question_Placeholder', 'For example, “I’d like to see other members from my community who use this product”.')
    };
    const logTelemetry = (action, actionModifier)=>{
        const payload = {
            page: location === null || location === void 0 ? void 0 : location.pathname,
            action,
            actionModifier,
            details: JSON.stringify({
                offerId: linkedInProductGroup.offerId
            })
        };
        SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
        logger.info(payload.details, {
            page: payload.page,
            action: payload.action,
            actionModifier: payload.actionModifier
        });
    };
    useEffect(()=>{
        if (shouldDisplayPopup(PopupType.LinkedinTeachingBubble)) {
            setTeachingBubbleMode(true);
            popupDisplayed(PopupType.LinkedinTeachingBubble);
            logTelemetry(Constants.Telemetry.Action.TeachingBubble, Constants.Telemetry.ActionModifier.Linkedin);
        }
    }, []);
    const onFeedbackClick = ()=>{
        setTeachingBubbleMode(false);
        setFeedbackMode(true);
        logTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.LinkedinFeedbackButton);
    };
    const onExternalEventLinkClick = ()=>{
        logTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.LinkedinEventLink);
    };
    const onFeedbackDismiss = ()=>{
        setFeedbackMode(false);
        logTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.LinkedinFeedbackDismiss);
        getNpsModule().npsUpdateLastDismissTime();
    };
    const onFeedbackSubmitted = ()=>{
        setFeedbackMode(false);
        getNpsModule().npsUpdateLastDismissTime();
        setFeedbackSentMode(true);
        logTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.LinkedinFeedbackSubmit);
    };
    const onTeachingBubbleCloseClick = ()=>{
        setTeachingBubbleMode(false);
    };
    const primaryButtonProps = {
        children: context.loc('Linkedin_Feedback_Action', 'Give feedback'),
        onClick: onFeedbackClick
    };
    const secondaryButtonProps = {
        children: context.loc('Linkedin_Teaching_Bubble_Close_Action', 'Close'),
        onClick: onTeachingBubbleCloseClick
    };
    const onTeachingBubblePreventDismiss = (event)=>{
        return event.type === 'scroll';
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: contentStyles.linkedin,
        children: [
            /*#__PURE__*/ _jsx(FeedbackSection, {
                onFeedbackClick: onFeedbackClick
            }),
            /*#__PURE__*/ _jsx(FeaturedCustomersList, {
                countryCode: countryCode,
                linkedInProductGroup: linkedInProductGroup,
                offerName: offerName,
                locale: locale
            }),
            /*#__PURE__*/ _jsx(UpcomingEventsList, {
                countryCode: countryCode,
                linkedInProductGroup: linkedInProductGroup,
                offerName: offerName,
                locale: locale,
                onExternalLinkClick: onExternalEventLinkClick
            }),
            feedbackMode && /*#__PURE__*/ _jsx(FeatureFeedback, {
                featureName: Constants.Telemetry.ActionModifier.Linkedin,
                viewContent: featureFeedbackContent,
                onDialogDismissed: onFeedbackDismiss,
                onFeedbackSubmitted: onFeedbackSubmitted,
                email: userEmail
            }),
            teachingBubbleMode && /*#__PURE__*/ _jsx(TeachingBubble, {
                target: parentTabQuery,
                calloutProps: {
                    directionalHint: DirectionalHint.bottomCenter,
                    preventDismissOnEvent: onTeachingBubblePreventDismiss
                },
                primaryButtonProps: primaryButtonProps,
                secondaryButtonProps: secondaryButtonProps,
                onDismiss: ()=>{
                    setTeachingBubbleMode(false);
                },
                headline: context.loc('Linkedin_Teaching_Bubble_title', 'Stay connected to the community'),
                children: context.loc('Linkedin_Teaching_Bubble_text', 'Explore and connect with other companies interested in this topic. Let us know if this is useful to you!')
            }),
            feedbackSentMode && /*#__PURE__*/ _jsx(FeatureFeedbackSubmittedModal, {
                hidden: !feedbackSentMode,
                onDismiss: ()=>{
                    setFeedbackSentMode(false);
                },
                context: context
            })
        ]
    });
};
export const LinkedinProductGroup = withRouter(LinkedinProductGroupFunction);
LinkedinProductGroupFunction.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
FeaturedCustomersList.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
UpcomingEventsList.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
FeedbackSection.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
